import i18n from "i18n";

/**
 * Parse timestamps from backends that store UTC but may omit `Z`.
 * `new Date("2026-05-04T12:00:00")` is treated as *local* wall time in ECMAScript;
 * appending `Z` when no offset is present keeps the instant aligned with UTC storage.
 */
export function parseBackendUtcDate(value: string): Date {
    const trimmed = (value ?? "").trim();
    if (!trimmed) {
        return new Date(NaN);
    }
    if (/Z$/i.test(trimmed)) {
        return new Date(trimmed);
    }
    if (/[+-]\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
        return new Date(trimmed);
    }
    const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
    return new Date(`${normalized}Z`);
}

function coerceToDate(value: string | Date | number | null | undefined): Date {
    if (value == null) {
        return new Date(NaN);
    }
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === "number") {
        return new Date(value);
    }
    return parseBackendUtcDate(value);
}

/** Dense date/time for tables and log timelines (uses the runtime locale). */
export const LOCAL_DATETIME_LOG: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
};

/** Medium date + short time (cards, file rows, schedules). */
export const LOCAL_DATETIME_MEDIUM_SHORT: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
};

/**
 * Format an API/backend instant in the user's local timezone and locale.
 * Strings are parsed with {@link parseBackendUtcDate} so naive UTC ISO values stay correct.
 */
export function formatLocalDateTime(
    value: string | Date | number | null | undefined,
    options: Intl.DateTimeFormatOptions = LOCAL_DATETIME_LOG,
    locale?: Intl.LocalesArgument
): string {
    if (value == null || value === "") {
        return "";
    }
    const date = coerceToDate(value);
    if (Number.isNaN(date.getTime())) {
        if (typeof value === "string") {
            return value;
        }
        return "";
    }
    const localeArg = typeof locale === "string" || Array.isArray(locale) ? locale : undefined;
    return new Intl.DateTimeFormat(localeArg ?? i18n.language ?? undefined, options).format(date);
}
